"use client"

import { cn } from "@/lib/utils";

import { Tip } from "@/components/fun-ui/Tip";
import { FeedbackIcon, GridIcon, GridSimplifyIcon } from "@/components/fun-ui/icons";
import AIColsSetter from "@/components/papers/PaperList/AIColsSetter";
import ExportPapersCSV from "@/components/papers/PaperList/ExportPapersCSV";
import SummarizeCreator from "@/components/papers/PaperList/SummarizeCreator";
import { PaperUpload } from "@/components/paper-upload";
import { importPlugin } from "@/components/paper-upload/plugins/import-plugin";
import { PaperId, Paper } from "@/stores/slices/paperSlice";
import SubjectCreator from "@/components/papers/PaperList/SubjectCreator";

import { Feedback } from "@/components/dashboard/Feedback";
import { UpgradeVipButton } from "@/components/payment/UpgradeVipButton";
import { Searcher } from "./Searcher";
import { useAiCol, useCols, useFetchPapers, useFilters, useSubjects, useUserVip } from "@/stores/appStore";
import { useSubjectId } from "@/components/subjects/hooks/usePathParams";
import { useDialog } from "@/hooks/useDialog";
import { Button } from "@/components/fun-ui/Button";
import { useSelection } from "@/hooks/useSelection";
import { Col } from "@/stores/slices/paperSlice";
import { useContainerResize } from "@/hooks/useContainerResize";
import { useCallback, useEffect, useRef, useState } from "react";
import type { OnBeforeUpload, OnBeforeUploadResult } from "@/components/paper-upload/hooks/useUpload";

export type ToolbarProps = {
  className?: string;
  dropArea?: HTMLElement | null;
  onUploaded: () => void;
  handleOnBeforeUpload: OnBeforeUpload;
  isSimplifyGrid: boolean;
  setIsSimplifyGrid: (isSimplifyGrid: boolean) => void;
  onNewColsApplied: (confirm: () => Promise<void>, cols: Col[]) => void;
}

export const Toolbar = ({
  className,
  dropArea,
  onUploaded,
  handleOnBeforeUpload,
  isSimplifyGrid,
  setIsSimplifyGrid,
  onNewColsApplied,
}: ToolbarProps) => {
  const subjectId = useSubjectId();
  const cols = useCols(subjectId);
  const {
    importPaper,
  } = useSubjects();
  const {
    limits,
  } = useUserVip();

  const {
    fetchPapers,
  } = useFetchPapers();
  const fetchPapersRef = useRef(fetchPapers);
  useEffect(() => { fetchPapersRef.current = fetchPapers; }, [fetchPapers]);

  const {
    setPaperCols,
  } = useAiCol();

  const { alert } = useDialog();

  const {
    searchString,
    setSearch,
    order,
  } = useFilters(subjectId);

  const {
    selectedIds,
  } = useSelection(subjectId);

  const [iconOnlyRight, setIconOnlyRight] = useState(false);
  const [iconOnlyLeft, setIconOnlyLeft] = useState(false);
  const {
    containerRef,
  } = useContainerResize({
    debug: false,
    onResize: ({ width }) => {
      if (width <= 1180) {
        setIconOnlyRight(true);
        if (width <= 1050) {
          setIconOnlyLeft(true);
        } else {
          setIconOnlyLeft(false);
        }
      } else {
        setIconOnlyRight(false);
        setIconOnlyLeft(false);
      }
    }
  });

  const handleSearch = useCallback(async (searchString: string) => {
    const search = searchString.trim();
    if (search) {
      setSearch(search);
    }
    await fetchPapersRef.current({
      subjectId,
      order,
      pageIndex: 0,
      search,
    });

    if (!search) {
      setSearch(search);
    }
  }, [subjectId, order, setSearch]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex items-center justify-between overflow-hidden flex-nowrap gap-4",
        className
      )}
    >
      <div className="flex-1 flex gap-4 flex-nowrap">
        <PaperUpload
          variant="button"
          plugins={subjectId ? ["url", {
            plugin: importPlugin,
            props: {
              onImport: async (paperIds: PaperId[], papers: Paper[]) => {
                await importPaper(subjectId, paperIds);
                return true;
              }
            }
          }, "local-folder", "local-file"] : ["url", "local-folder", "local-file"]}
          subjectId={subjectId}
          onBeforeUpload={handleOnBeforeUpload}
          onUploaded={onUploaded}
          dropArea={dropArea}
          label={subjectId ? "导入文献" : "上传文献"}
        />
        <AIColsSetter
          cols={cols}
          iconOnly={iconOnlyLeft}
          applyNewCols={async (colKeys, prevCols, cols) => {
            const limited = limits!.aiColLimit > 0 ? limits!.aiColLimit : Infinity;
            if (colKeys.length > limited) {
              const content = `最多可同时展示 ${limited} 列，升级 Plus 会员可全部展示。`
              await alert({
                title: '提示',
                content,
                ConfirmButton: <UpgradeVipButton />,
                size: 'lg',
              });
              return;
            }
            if (colKeys.length <= limited && prevCols.length > limited) {
              const content = `最多可同时展示 ${limited} 列，当前已超，减少后升级 Plus 会员可全部展示。`;
              const confirm = await alert({
                title: '提示',
                content,
                size: 'lg',
              });
              if (!confirm) {
                return;
              }
            }

            const confirm = async () => {
              const newCheckedKey = colKeys.filter(key => !prevCols.includes(key))[0];
              setIsSimplifyGrid(false);
              await setPaperCols(colKeys, subjectId);
              if (newCheckedKey && dropArea) {
                const target = dropArea.querySelector(
                  `[data-colkey="${newCheckedKey}"]`
                )?.parentElement as HTMLElement;
                target?.scrollIntoView({
                  behavior: 'smooth',
                  block: "start",
                  inline: 'center'
                });
              }
            };
            onNewColsApplied(confirm, cols);
          }}
        />
        {
          !subjectId ? (
            <SubjectCreator
              ids={selectedIds}
              iconOnly={iconOnlyLeft}
            />
          ) : null
        }
        <SummarizeCreator
          ids={selectedIds}
          iconOnly={iconOnlyLeft}
        />
      </div>
      <div className="flex-1 flex gap-4 flex-nowrap justify-end">
        <Searcher
          defaultValue={searchString}
          onSearch={handleSearch}
        />
        <Feedback scene="aiCol" sceneContents={{
          scene_id: '',
        }}>
          <div>
            <Tip label={iconOnlyRight ? "反馈一下" : ""} asChild>
              <div>
                <Button variant="outline" className={cn(
                  "flex items-center gap-1 whitespace-nowrap w-auto px-4",
                  iconOnlyRight && "w-10 px-0",
                )}>
                  <FeedbackIcon />
                  <span className={cn(
                    iconOnlyRight ? "hidden" : "inline-block"
                  )} >反馈一下</span>
                </Button>
              </div>
            </Tip>
          </div>
        </Feedback>
        <ExportPapersCSV
          ids={selectedIds}
          subjectId={subjectId}
          iconOnly={iconOnlyRight}
        />
        <Tip label={isSimplifyGrid ? "AI 列表格模式" : "精简表格模式"}>
          <Button
            variant="outline"
            className="px-3"
            onClick={() => {
              setIsSimplifyGrid(!isSimplifyGrid)
            }}
          >
            {
              isSimplifyGrid ? <GridIcon /> : <GridSimplifyIcon />
            }
          </Button>
        </Tip>
      </div>
    </div>
  )
}